import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Box } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledTextField,
  ControlledDateTimeField,
  ControlledSelect,
  ControlledMediaUpload,
} from '@/components/inputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { schema } from './validationSchema'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { FormComponentProps } from '@/types'
import {
  REPTILES_SPECIE_OPTIONS,
  OTHER_SPECIE_OPTION_VALUE,
  REPTILES_COUNTING_METHOD_OPTIONS,
  REPTILES_BEHAVIOUR_OPTIONS,
  OTHER_GEDRAG_OPTION_VALUE,
} from '../fieldsOptions'

export const ReptilesForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      other_specie: '',
      amount: '',
      counting_method: '',
      behaviour: '',
      other_behaviour: '',
      behaviour_notes: '',
      observed_at: '',
      photos: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const watchedSpecie = methods.watch('specie')

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <ControlledSelect
            control={methods.control}
            name="specie"
            label={t('observation-log.fieldLabels.specie')}
            options={REPTILES_SPECIE_OPTIONS}
            errorMessagePrefix=""
          />
          {watchedSpecie === OTHER_SPECIE_OPTION_VALUE && (
            <ControlledTextField
              control={methods.control}
              name="other_specie"
              placeholder={t('observation-log.fieldLabels.otherSpecie')}
              errorMessagePrefix=""
            />
          )}
          <ControlledTextField
            control={methods.control}
            name="amount"
            placeholder={t('observation-log.fieldLabels.amount')}
            type="number"
            errorMessagePrefix=""
          />
          <ControlledSelect
            control={methods.control}
            name="counting_method"
            label={t('observation-log.fieldLabels.countingMethod')}
            options={REPTILES_COUNTING_METHOD_OPTIONS}
            errorMessagePrefix=""
          />
        </Stack>
        <Box width="33%">
          <ControlledSelect
            control={methods.control}
            name="behaviour"
            label={t('observation-log.fieldLabels.behaviour')}
            options={REPTILES_BEHAVIOUR_OPTIONS}
            errorMessagePrefix=""
          />
        </Box>
        {methods.watch('behaviour') === OTHER_GEDRAG_OPTION_VALUE && (
          <Box width="33%">
            <ControlledTextField
              control={methods.control}
              name="other_behaviour"
              placeholder={t('observation-log.fieldLabels.otherBehaviour')}
              errorMessagePrefix=""
            />
          </Box>
        )}
        <ControlledTextField
          control={methods.control}
          name="behaviour_notes"
          placeholder={t('observation-log.fieldLabels.behaviourNotes')}
          multiline={true}
          rows={3}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          errorMessagePrefix=""
        />
        <ControlledDateTimeField
          name="observed_at"
          control={methods.control}
          label={t('observation-log.fieldLabels.observedAt')}
          errorMessagePrefix=""
        />
        <ControlledMediaUpload
          name="photos"
          control={methods.control}
          label={t('observation-log.fieldLabels.media')}
          errorMessagePrefix=""
        />

        <ObservationLogFormActions
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          isLoading={isLoading}
          methods={methods}
        />
      </Stack>
    </FormProvider>
  )
}
